'use client';
import { StatsigOnDeviceEvalClient } from '@statsig/js-on-device-eval-client';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { clearLocalFlag, getLocalFlag, setLocalFlag } from './local-flags';

import type { DynamicConfigName, Feature, StatsigTier } from './flags';
import type { StatsigUser } from '@statsig/js-on-device-eval-client';
import type { FC, ReactElement, ReactNode } from 'react';

export type StatsigImpressionListener = (impression: { feature: string; treatment: 'on' | 'off' }) => void;

export type { DynamicConfigName, Feature, StatsigTier } from './flags';
export { setLocalFlag, clearLocalFlag };

type App = 'clients' | 'sessions';

// Attribute values accepted for gate targeting. Mirrors the primitive shape Statsig custom fields
// accept; number arrays are coerced to string arrays before evaluation (see toStatsigCustom).
type FlagAttributes = Record<string, string | number | boolean | Array<string | number>>;
type FlagKey = string | number;

type Props = {
  app: App;
  attributes?: { demo?: boolean };
  statsigClientKey?: string;
  environment?: StatsigTier;
  children: ((args: { isReady: boolean }) => ReactNode) | ReactNode;
  // Receives one call per gate evaluation; boolean values are normalised to 'on'/'off' so callers
  // can forward them straight to their analytics sink.
  statsigImpressionListener?: StatsigImpressionListener;
};

// Holds the active on-device Statsig client so useFeatureFlags can evaluate gates against a
// per-call user. The on-device client downloads the full ruleset once and evaluates any user
// object synchronously — so there is no global identity to mutate.
const StatsigClientCtx = createContext<StatsigOnDeviceEvalClient | null>(null);

// Lowest-priority attributes seeded by the provider so every evaluation — including anonymous,
// pre-login ones — carries app/demo/hostname. Per-user and per-call attributes layer on top.
const StatsigBaseCtx = createContext<{ app: App; demo?: boolean; hostname?: string } | null>(null);

type StatsigCustom = NonNullable<StatsigUser['custom']>;

// Statsig gate names follow lower_snake_case convention; our Feature constants are UPPER_SNAKE.
function toGateName(flag: Feature): string {
  return flag.toLowerCase();
}

const Ctx = createContext<{ userId?: string; attributes?: FlagAttributes } | undefined>(undefined);

function useUserFeatureFlags() {
  return useContext(Ctx);
}

// Coerce number arrays to string arrays so attribute values match Statsig's custom primitives
// (Array<string> only).
function toStatsigCustom(attributes: FlagAttributes): StatsigCustom {
  return Object.fromEntries(
    Object.entries(attributes).map(([name, value]) => [name, Array.isArray(value) ? value.map(String) : value]),
  );
}

function toStatsigUserId(key: FlagKey | undefined): string | undefined {
  return key === undefined ? undefined : String(key);
}

type UserFeatureFlagProviderProps = {
  children: (args: { isReady: boolean }) => ReactElement;
  app: App;
  userId?: string;
  userAttributes?: {
    team_id?: string;
    type?: string;
  };
  anonymousAttributes?: Props['attributes'];
};

// Publishes the per-user context that useFeatureFlags evaluates gates against. The on-device
// client needs no per-user sync — it evaluates whatever user object it's handed — so there's no
// per-user readiness to await here; the ruleset download is gated once by FeatureFlagsProvider.
export const UserFeatureFlagProvider: FC<UserFeatureFlagProviderProps> = ({
  children,
  userAttributes,
  anonymousAttributes,
  app,
  userId,
}) => {
  const value = useMemo(() => {
    return {
      userId,
      attributes: {
        app,
        pathname: (typeof window !== 'undefined' && window.location.pathname) || '',
        ...userAttributes,
        ...anonymousAttributes,
      },
    };
  }, [userAttributes, anonymousAttributes, app, userId]);

  return <Ctx.Provider value={value}>{children({ isReady: true })}</Ctx.Provider>;
};

// Build the Statsig user for one evaluation by layering attributes: provider base
// (app/demo/hostname), then per-user attributes bound by UserFeatureFlagProvider, then any per-call
// `att` (which wins). Layering — rather than an `att ?? userDetails` replace — keeps a partial
// per-call `att` (e.g. just { team_id } in a room) from dropping bound fields like pathname/type.
// Shared by gate and dynamic-config reads so both target on the same user.
function useStatsigUser(att?: FlagAttributes, key?: FlagKey): StatsigUser {
  const userDetails = useUserFeatureFlags();
  const baseAttributes = useContext(StatsigBaseCtx);
  return useMemo<StatsigUser>(() => {
    const custom: StatsigCustom = {
      ...baseAttributes,
      ...(userDetails?.attributes ? toStatsigCustom(userDetails.attributes) : {}),
      ...(att ? toStatsigCustom(att) : {}),
    };
    return {
      userID: toStatsigUserId(key) ?? userDetails?.userId ?? 'anonymous',
      custom,
    };
  }, [att, key, userDetails, baseAttributes]);
}

function useFeatureFlags<T extends Feature = Feature>(
  flags: T[],
  att?: FlagAttributes,
  key?: FlagKey,
): Record<T, boolean> {
  const statsigClient = useContext(StatsigClientCtx);
  const statsigUser = useStatsigUser(att, key);

  return useMemo(() => {
    const statsigReady = statsigClient?.loadingStatus === 'Ready';

    return flags.reduce<Record<T, boolean>>(
      (acc, flag) => {
        const localOverride = getLocalFlag(flag);
        if (localOverride !== undefined) {
          acc[flag] = localOverride === 'on';
          return acc;
        }

        // Only evaluate against a loaded ruleset; an unready client returns NoValues (false) for
        // every gate, so checking it would silently disable the flag and log a misleading exposure.
        acc[flag] = statsigClient && statsigReady ? statsigClient.checkGate(toGateName(flag), statsigUser) : false;
        return acc;
      },
      {} as Record<T, boolean>,
    );
  }, [flags, statsigClient, statsigUser]);
}

// Reads a Statsig dynamic config's JSON value for a per-call user, mirroring useFeatureFlags'
// attribute layering (so a room can target it by { studyId, team_id } the same way it targets gates).
// Returns undefined until the ruleset is loaded — an unready on-device client returns an empty
// config, which we surface as "no value" so callers keep their own defaults rather than treating
// not-ready as an empty override. Logs a config exposure by default, like the gates do.
function useDynamicConfig(
  configName: DynamicConfigName,
  att?: FlagAttributes,
  key?: FlagKey,
): Record<string, unknown> | undefined {
  const statsigClient = useContext(StatsigClientCtx);
  const statsigUser = useStatsigUser(att, key);

  return useMemo(() => {
    const statsigReady = statsigClient?.loadingStatus === 'Ready';
    if (!statsigClient || !statsigReady) {
      return undefined;
    }
    return statsigClient.getDynamicConfig(configName, statsigUser).value;
  }, [configName, statsigClient, statsigUser]);
}

// Owns the on-device client lifecycle. Hooks must be unconditional, so this lives in its own
// subcomponent that's only mounted when a clientKey is provided — the "no key" case is a graceful
// no-op where every gate reads false.
function StatsigSection({
  sdkKey,
  tier,
  impressionListener,
  children,
}: {
  sdkKey: string;
  tier?: StatsigTier;
  impressionListener?: StatsigImpressionListener;
  children: (statsigReady: boolean) => ReactNode;
}) {
  const [client] = useState(() => new StatsigOnDeviceEvalClient(sdkKey, tier ? { environment: { tier } } : null));
  const [isReady, setIsReady] = useState(false);

  // Forward every gate evaluation to the listener.
  useEffect(() => {
    if (!impressionListener) return;
    const handler = (event: { gate: { name: string; value: boolean } }) => {
      // Upper-case the gate name so Datadog sees the UPPER_SNAKE identifier matching our Feature
      // constants; Statsig gate names are a 1:1 lowercase of those (see toGateName).
      impressionListener({ feature: event.gate.name.toUpperCase(), treatment: event.gate.value ? 'on' : 'off' });
    };
    client.on('gate_evaluation', handler);
    return () => client.off('gate_evaluation', handler);
  }, [client, impressionListener]);

  // Downloads the ruleset once; afterwards every checkGate is synchronous for any user.
  // initializeAsync resolves (not rejects) on a failed download with `success: false` — surface
  // that loudly, because an unloaded ruleset evaluates every gate to false. Mark ready regardless
  // so a Statsig outage can't block the app: gates degrade to false (gated on loadingStatus in
  // useFeatureFlags) rather than hanging on the loading screen.
  useEffect(() => {
    let active = true;
    client
      .initializeAsync()
      .then(result => {
        if (!result.success) {
          console.error('[feature-flags] Statsig ruleset failed to load — gates will read false', result.error);
        }
      })
      .catch(err => {
        console.error('[feature-flags] Statsig initializeAsync threw', err);
      })
      .finally(() => {
        if (active) setIsReady(true);
      });
    return () => {
      active = false;
    };
  }, [client]);

  return <StatsigClientCtx.Provider value={client}>{children(isReady)}</StatsigClientCtx.Provider>;
}

const FeatureFlagsProvider = ({
  children,
  statsigClientKey,
  environment,
  app,
  attributes,
  statsigImpressionListener,
}: Props) => {
  const baseAttributes = useMemo(
    () => ({
      app,
      demo: attributes?.demo,
      hostname: typeof window !== 'undefined' ? window.location.hostname : undefined,
    }),
    [app, attributes?.demo],
  );

  return (
    <StatsigBaseCtx.Provider value={baseAttributes}>
      {statsigClientKey ? (
        <StatsigSection sdkKey={statsigClientKey} tier={environment} impressionListener={statsigImpressionListener}>
          {statsigReady => (typeof children === 'function' ? children({ isReady: statsigReady }) : children)}
        </StatsigSection>
      ) : (
        <>{typeof children === 'function' ? children({ isReady: true }) : children}</>
      )}
    </StatsigBaseCtx.Provider>
  );
};

export { FeatureFlagsProvider, useDynamicConfig, useFeatureFlags };
